// Real <video>/<audio> elements and countdown digits follow the engine (7.6E;
// moved from the Studio to @interactads/creative-runtime in 7.6I so the served
// ad and the preview drive media the same way). Called from the frame loop.
//
// The engine's logical state decides (play/pause, mute, volume, position
// within 350 ms); the elements never decide anything. The only things read
// back are the two facts the browser alone knows, and only when the host
// wires them (served ad, engine option mediaStart "confirmed"):
//   - the element really started playing        → host.mediaPlaying(id)
//   - the browser refused autoplay (NotAllowed)  → host.mediaBlocked(id)
// Browser policies are respected, never worked around: sound only after a
// gesture (the engine already keeps autoplay muted), no retry loop on refusal.

import type { MediaView } from "./engine.js";
import { countdownText } from "./ui-text.js";

export interface MediaHost {
  readonly mediaView: (id: string) => MediaView | undefined;
  /** Served ad: the element reported `playing` (confirms a pending autoplay). */
  readonly mediaPlaying?: (id: string) => void;
  /** Served ad: play() was refused by the browser's autoplay policy. */
  readonly mediaBlocked?: (id: string) => void;
  /** Served ad: the element cannot load/decode the media (MediaError, NotSupportedError). */
  readonly mediaFailed?: (id: string) => void;
}

/** Elements with a play() in flight (never asked twice while the browser decides). */
const inFlight = new WeakSet<HTMLMediaElement>();
/** Elements whose `playing` / `error` listeners are already attached. */
const wired = new WeakSet<HTMLMediaElement>();

export function syncMedia(root: ParentNode | null, host: MediaHost, running: boolean): void {
  if (!root) return;
  for (const media of root.querySelectorAll<HTMLMediaElement>("[data-media]")) {
    const id = media.dataset.media ?? "";
    const view = host.mediaView(id);
    if (!view) continue;
    if (host.mediaPlaying && !wired.has(media)) {
      wired.add(media);
      media.addEventListener("playing", () => {
        host.mediaPlaying?.(id);
      });
      // A source the browser cannot fetch or decode (codec not supported, corrupt bytes).
      media.addEventListener("error", () => {
        if (media.error) host.mediaFailed?.(id);
      });
    }
    if (view.failed) {
      if (!media.paused) media.pause();
      continue;
    }
    media.muted = view.muted;
    media.volume = Math.max(0, Math.min(1, view.volume));
    const target = view.position / 1_000;
    if (media.readyState >= 1 && Math.abs(media.currentTime - target) > 0.35)
      media.currentTime = Number.isFinite(media.duration) ? Math.min(target, media.duration) : target;
    const shouldPlay = running && (view.playing || view.pending);
    if (shouldPlay && media.paused && !inFlight.has(media)) {
      inFlight.add(media);
      let attempt: Promise<void> | undefined;
      try {
        attempt = media.play();
      } catch {
        attempt = undefined;
      }
      void Promise.resolve(attempt)
        .then(() => {
          // Already playing (no `playing` event when it never paused): confirm it too.
          if (!media.paused) host.mediaPlaying?.(id);
        })
        .catch((e: unknown) => {
          // Only an autoplay-policy refusal is "blocked"; an AbortError (pause() interrupted
          // play()) or a decode error is not.
          const name = typeof e === "object" && e !== null ? (e as { name?: unknown }).name : undefined;
          if (name === "NotAllowedError" && view.pending) host.mediaBlocked?.(id);
          // No playable source for this element in this browser: final, never retried.
          else if (name === "NotSupportedError") host.mediaFailed?.(id);
        })
        .finally(() => {
          inFlight.delete(media);
        });
    }
    if (!shouldPlay && !media.paused) media.pause();
  }
}

/** "m:ss / m:ss" of a media's logical position and duration. */
export function mediaTimeText(view: Pick<MediaView, "position" | "duration">): string {
  const fmt = (ms: number) => {
    const s = Math.max(0, Math.round(ms / 1_000));
    return `${String(Math.floor(s / 60))}:${String(s % 60).padStart(2, "0")}`;
  };
  return `${fmt(view.position)} / ${fmt(view.duration)}`;
}

/**
 * Rewrites the served player's time labels from the engine's logical clock (frame loop): the position
 * advances without a discrete engine change, so the renderer's updaters alone would move it only at
 * the quartile events (runtime 1.2.3).
 */
export function syncMediaTimes(root: ParentNode | null, host: Pick<MediaHost, "mediaView">): void {
  if (!root) return;
  for (const node of root.querySelectorAll<HTMLElement>("[data-media-time]")) {
    const view = host.mediaView(node.dataset.mediaTime ?? "");
    if (!view) continue;
    const text = mediaTimeText(view);
    if (node.textContent !== text) node.textContent = text;
  }
}

/** Rewrites the countdown digits under `root` from the engine's logical clock (frame loop). */
export function syncCountdowns(root: ParentNode | null, host: { timerRemaining: (id: string) => number }): void {
  if (!root) return;
  for (const node of root.querySelectorAll<HTMLElement>("[data-countdown]")) {
    const text = countdownText(
      host.timerRemaining(node.dataset.countdown ?? ""),
      node.dataset.format === "ss" ? "ss" : "mm:ss"
    );
    if (node.textContent !== text) node.textContent = text;
  }
}
